import * as React from 'react';
import { useTheme } from 'styled-components/macro';
import {
  LoadingIndicator,
  ModalDelete,
  ModalVideoAttributes,
} from 'lib/components';

import { NoAttributes, AttributesList } from '../components';
import {
  Container,
  HeaderWrapper,
  MainWrapper,
} from 'lib/components/styles/layout';
import { MdAdd } from 'react-icons/md';
import { Heading } from 'lib/components/styles/typography';
import { Button } from 'react-covideo-common';
import { useRemoveAttribute } from 'lib/api/videoAttributes/useRemoveVideoAttribute';
import { useUpdateOrderAttributes } from 'lib/api/videoAttributes/useUpdateOrderAttributes';
import { useGetAllVideoAttributesQuery } from 'lib/api/videoAttributes/useGetAllVideoAttributesQuery';
import {
  TableContextProvider,
  TableFooter,
  TablePaginationNew,
  TablePaginationSizeNew,
} from 'lib/components';
import { useTranslation } from 'react-i18next';
import { useGTMAnalytics } from 'lib/hooks/GTM/useGTMAnalytics';
import { CustomAttributesInteractions } from 'lib/hooks/GTM/actions/customAttribute/types';

export const Main = () => {
  const { trackGTMCustomAttributeInteractionsEvent } = useGTMAnalytics();
  const { t } = useTranslation();
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [selectedAttribute, setSelectedAttribute] = React.useState<any>({});
  const [reorderLoading, setReorderLoading] = React.useState(false);
  const themes = useTheme();
  const { mutateAsync: removeAttribute } = useRemoveAttribute();
  const { mutateAsync: updateOrder } = useUpdateOrderAttributes();
  const [page, setPage] = React.useState(0);
  const [size, setSize] = React.useState(10);

  const { data, refetch, isLoading } = useGetAllVideoAttributesQuery({
    page: page,
    size: size,
  });
  const { attributes: attributesList, count } = data || {
    attributes: [],
    count: 0,
  };

  const onPaginationChange = ({
    page: newPage,
    size: newSize,
  }: {
    page: number;
    size: number;
  }) => {
    setSize(newSize);
    setPage(newSize !== size ? 0 : newPage);
  };

  const openCreateAttributeModal = () => {
    setSelectedAttribute({
      title: '',
      slug: '',
      type: 'text',
      default: '',
      dropdownValues: [],
    });
    setShowCreateModal(true);
  };

  const openEditAttributeModal = (selectedAttribute: any) => {
    setSelectedAttribute(selectedAttribute);
    setShowCreateModal(true);
  };

  const openDeleteAttributeModal = (selectedAttribute: any) => {
    setSelectedAttribute(selectedAttribute);
    setShowDeleteModal(true);
  };

  const updateAttributes = async (updatedAttributes: any) => {
    setReorderLoading(true);
    await updateOrder(updatedAttributes);
    await refetch();

    setReorderLoading(false);
  };

  return (
    <Container>
      <MainWrapper resetPadding={false}>
        <LoadingIndicator isLoading={isLoading} />
        {!isLoading && (
          <>
            <HeaderWrapper>
              <Heading>{t('Custom Video Attributes')}</Heading>
              <Button
                text={t('Create New')}
                onClick={() => openCreateAttributeModal()}
                icon={<MdAdd />}
              />
            </HeaderWrapper>

            {!attributesList.length && (
              <NoAttributes
                showModal={() => openCreateAttributeModal()}
                color={themes.colors.primary[100]}
              />
            )}
            {!!attributesList.length && (
              <TableContextProvider
                total={count}
                onChange={onPaginationChange}
                initPage={page}
                initSize={size}
              >
                <AttributesList
                  attributes={attributesList}
                  showEditModal={openEditAttributeModal}
                  showDeleteModal={openDeleteAttributeModal}
                  updateAttributesOrder={updateAttributes}
                  reorderLoading={reorderLoading}
                />
                <TableFooter>
                  <TablePaginationNew />
                  <TablePaginationSizeNew text={t('Show video attributes:')} />
                </TableFooter>
              </TableContextProvider>
            )}
            {showCreateModal && (
              <ModalVideoAttributes
                attributes={attributesList}
                selectedAttribute={selectedAttribute}
                handleModalClose={() => {
                  refetch();
                  setShowCreateModal(false);
                }}
              />
            )}
            {showDeleteModal && (
              <ModalDelete
                whiteButtonText={t('Yes, Delete')}
                orangeButtonText={t("No, Don't Delete")}
                title={t('Delete Custom Attribute?')}
                text={t(
                  'You’re going to delete {{title}} attribute. This action can’t be undone.',
                  { title: selectedAttribute.title }
                )}
                handleModalClose={() => setShowDeleteModal(false)}
                onClickWhiteButton={async () => {
                  await removeAttribute(selectedAttribute.attributeId);
                  trackGTMCustomAttributeInteractionsEvent?.({
                    action: CustomAttributesInteractions.REMOVED,
                    title: selectedAttribute.title,
                  });
                  refetch();
                  setShowDeleteModal(false);
                }}
                onClickOrangeButton={() => setShowDeleteModal(false)}
              />
            )}
          </>
        )}
      </MainWrapper>
    </Container>
  );
};
